import { isHttpError, useAuth } from "@mb-pro-ui/utils";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ReloadIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "wouter";
import { useLocale } from "./locales/LocaleProvider";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const Login = () => {
  const { formatMessage } = useIntl();
  const { setLocale, locale, locales } = useLocale();

  const [showPassword, setShowPassword] = useState(false);

  const [promptForInstall, setPromptForInstall] = useState<(() => void) | null>(
    null,
  );

  const {
    signin: { isPending, isError, isSuccess, mutate, error },
    user,
  } = useAuth();

  const errorMessage = useMemo(() => {
    if (error && isHttpError(error) && error.status === 401) {
      return formatMessage({
        defaultMessage: "Incorrect username or password",
      });
    } else if (error) {
      return (
        formatMessage({ defaultMessage: "An error occurred during login:" }) +
        error.message
      );
    } else {
      return "\u00a0";
    }
  }, [error, formatMessage]);

  const [_, navigate] = useLocation();

  const handleLanguageSelect = (lang: string) => {
    setLocale(lang);
    localStorage.setItem("lang", lang);
  };

  useEffect(() => {
    if (user) {
      if (
        typeof history.state === "object" &&
        !!history.state &&
        typeof history.state.from === "string"
      ) {
        navigate(history.state.from, { replace: true });
      } else {
        navigate("/start-session", { replace: true });
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setPromptForInstall(() => () => {
        e.prompt();
        e.userChoice.then(() => {
          setPromptForInstall(null);
        });
      });
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        background: (theme) => theme.palette.primary.main,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        "&::before": {
          content: '""',
          background:
            "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
        },
      }}
    >
      <Box sx={{ overflowY: "scroll", height: "100%" }}>
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: 4,
            px: 3,
            position: "relative",
            zIndex: 1,
            background: "transparent",
          }}
        >
          <Card
            elevation={24}
            sx={{
              width: "100%",
              maxWidth: 400,
              borderRadius: 4,
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              overflow: "visible",
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                })}
                display="flex"
                justifyContent="center"
                mb={2}
              >
                {locales.map((lang) => (
                  <ButtonBase
                    sx={{
                      "&:not(:first-of-type):before": {
                        content: '"/"',
                        paddingLeft: "0.2rem",
                        paddingRight: "0.2rem",
                        textDecoration: "initial",
                      },
                    }}
                    key={lang}
                    onClick={() => handleLanguageSelect(lang)}
                    disabled={locale === lang}
                  >
                    <Typography
                      sx={[locale === lang && { fontWeight: "bold" }]}
                    >
                      {lang}
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <img
                  src="raptor-logo.svg"
                  style={{ width: "120px", height: "120px" }}
                ></img>
              </Box>
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 1,
                }}
              >
                {formatMessage({ defaultMessage: "Sign in" })}
              </Typography>
              <Typography
                variant="body2"
                align="center"
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                {formatMessage({ defaultMessage: "Enter your login details" })}
              </Typography>
              <Box
                component="form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const username = (
                    e.currentTarget.elements.namedItem(
                      "username",
                    ) as HTMLInputElement
                  ).value;
                  const password = (
                    e.currentTarget.elements.namedItem(
                      "password",
                    ) as HTMLInputElement
                  ).value;
                  mutate({ username, password });
                }}
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <TextField
                  name="username"
                  label={formatMessage({ defaultMessage: "Username" })}
                  required
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="username"
                  autoFocus
                  disabled={isPending}
                  error={isError}
                  variant="outlined"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 56,
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "white",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                      },
                    },
                  }}
                />

                <TextField
                  type={showPassword ? "text" : "password"}
                  name="password"
                  label={formatMessage({ defaultMessage: "Password" })}
                  required
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="current-password"
                  disabled={isPending}
                  error={isError}
                  helperText={errorMessage}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isPending}
                          sx={{ color: "text.secondary" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 56,
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "white",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                      },
                    },
                  }}
                />

                {isPending || isSuccess ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 2,
                      mb: 1,
                    }}
                  >
                    <CircularProgress size={48} />
                  </Box>
                ) : (
                  <Button
                    type="submit"
                    disabled={isPending}
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                      height: 56,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                      "&:active": {
                        transform: "translateY(0px)",
                      },
                    }}
                  >
                    {formatMessage({ defaultMessage: "Sign in" })}
                  </Button>
                )}
              </Box>
              <Box>
                {promptForInstall && (
                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "center" }}
                  >
                    <Button
                      variant="text"
                      color="info"
                      onClick={() => promptForInstall()}
                    >
                      {formatMessage({ defaultMessage: "Install APP" })}
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Container>

        <Box
          component="footer"
          sx={{
            textAlign: "center",
            py: 3,
            px: 3,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Button
            variant="text"
            size="small"
            startIcon={<ReloadIcon />}
            onClick={() => {
              window.location.reload();
            }}
            sx={{
              minHeight: "unset",
              color: "rgba(255, 255, 255, 0.8)",
            }}
          >
            v{APP_VERSION}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
